import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Form, FormControl, FormField, FormItem } from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import BigNumber from 'bignumber.js';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { z } from 'zod';

import { useConversorMlhEGotas } from './context';
import { EConversorMlhEGotasType } from './context/IUseConversorMlhEGotas';

const formSchema = z.object({
  type: z.enum(EConversorMlhEGotasType, {
    message: errorsValidation.required,
  }),
  quantity: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function ConversationMlDrops() {
  const [mlH, setMlH] = useState<number>(15);
  const [macroDrops, setMacroDrops] = useState<number>(0);
  const [microDrops, setMicroDrops] = useState<number>(0);

  const { quantity, setQuantity, setType, type } = useConversorMlhEGotas();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      type,
      quantity: quantity.toFixed(1),
    },
  });

  const calculateMicroDrops = useCallback((): void => {
    if (type === EConversorMlhEGotasType.MlHour) {
      // FÓRMULA: (ml/h * 60) / 60
      const quantityParsed = new BigNumber(quantity);
      const stepOne = quantityParsed.multipliedBy(60);
      const result = stepOne.dividedBy(60);
      setMicroDrops(result.toNumber());
    }
    if (type === EConversorMlhEGotasType.MacroDrops) {
      // FÓRMULA: macrogotas * 3
      const quantityParsed = new BigNumber(quantity);
      const result = quantityParsed.multipliedBy(3);
      setMicroDrops(result.toNumber());
    }
    if (type === EConversorMlhEGotasType.MicroDrops) {
      setMicroDrops(quantity);
    }
  }, [quantity, type]);

  useEffect(() => {
    calculateMicroDrops();
  }, [calculateMicroDrops]);

  const calculateMacroDrops = useCallback((): void => {
    if (type === EConversorMlhEGotasType.MlHour) {
      // FÓRMULA: (ml/h * 20) / 60
      const quantityParsed = new BigNumber(quantity);
      const stepOne = quantityParsed.multipliedBy(20);
      const result = stepOne.dividedBy(60);
      setMacroDrops(result.toNumber());
    }
    if (type === EConversorMlhEGotasType.MacroDrops) {
      setMacroDrops(quantity);
    }
    if (type === EConversorMlhEGotasType.MicroDrops) {
      // FÓRMULA: (microgotas * 0,33333) / 1
      const quantityParsed = new BigNumber(quantity);
      const stepOne = quantityParsed.multipliedBy(0.33333);
      const result = stepOne.dividedBy(1);
      setMacroDrops(result.toNumber());
    }
  }, [quantity, type]);

  useEffect(() => {
    calculateMacroDrops();
  }, [calculateMacroDrops]);

  const calculateMlh = useCallback((): void => {
    if (type === EConversorMlhEGotasType.MlHour) {
      setMlH(quantity);
    }
    if (type === EConversorMlhEGotasType.MacroDrops) {
      // FÓRMULA: [(macrogotas * 1) / 20] * 60
      const quantityParsed = new BigNumber(quantity);
      const stepOne = quantityParsed.multipliedBy(1);
      const stepTwo = stepOne.dividedBy(20);
      const result = stepTwo.multipliedBy(60);
      setMlH(result.toNumber());
    }
    if (type === EConversorMlhEGotasType.MicroDrops) {
      // FÓRMULA: [(microgotas * 0,0166666) / 1] * 60
      const quantityParsed = new BigNumber(quantity);
      const stepOne = quantityParsed.multipliedBy(0.0166666);
      const stepTwo = stepOne.dividedBy(1);
      const result = stepTwo.multipliedBy(60);
      setMlH(result.toNumber());
    }
  }, [quantity, type]);

  useEffect(() => {
    calculateMlh();
  }, [calculateMlh]);

  const handleChangeType = useCallback(
    (value: EConversorMlhEGotasType): void => {
      setType(value);
    },
    [setType],
  );

  const handleChangeQuantity = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setQuantity(valueParsed);
    },
    [setQuantity],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'type' && value.type) {
        handleChangeType(value.type);
      }
      if (name === 'quantity' && value.quantity) {
        handleChangeQuantity(value.quantity);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div>
      <PageTitle hasBackButton title="Conversão ml/h e gotas/min" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {[
                        {
                          label: 'mL/hora',
                          value: EConversorMlhEGotasType.MlHour,
                        },
                        {
                          label: 'macrogotas/min',
                          value: EConversorMlhEGotasType.MacroDrops,
                        },
                        {
                          label: 'microgotas/min',
                          value: EConversorMlhEGotasType.MicroDrops,
                        },
                      ].map((item) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={item.value}
                          value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="quantity"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <Label className="text-foreground text-sm font-light">
              é equivalente a:
            </Label>
          </div>

          <div className="mx-auto mb-4 block w-fit space-y-4">
            <div className="grid grid-cols-[min-content_min-content] items-center gap-2">
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {microDrops.toFixed(1)}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                microgotas/min
              </Label>
            </div>

            <div className="grid grid-cols-[min-content_min-content] items-center gap-2">
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {macroDrops.toFixed(1)}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                macrogotas/min
              </Label>
            </div>

            <div className="grid grid-cols-[min-content_min-content] items-center gap-2">
              <p>
                <span className="text-tertiary text-lg font-bold">
                  {mlH.toFixed(1)}
                </span>{' '}
              </p>
              <Label className="text-foreground text-sm font-light">
                mL/hora
              </Label>
            </div>
          </div>

          <div className="flex flex-col items-center space-y-2">
            <Label className="text-foreground text-sm font-light">
              1 mL = 20 macrogotas = 60 microgotas
            </Label>
            <Label className="text-foreground text-sm font-light">
              1 macrogota = 3 microgotas = 0,05 mL
            </Label>
            <Label className="text-foreground text-sm font-light">
              1 microgota = 0,33 macrogota = 0,017 mL
            </Label>
          </div>

          <p className="text-foreground mt-4 text-center text-xs font-light">
            Referência: Conselho Regional de Enfermagem (COREN). Boas práticas:
            Cálculo seguro. Volume I: Revisão das operações básicas, 2011.
          </p>
        </form>
      </Form>
    </div>
  );
}

export default ConversationMlDrops;
